import React, { useState } from 'react';
import './TamilNaduMap.css';
import { districtPrograms } from '../../data/districtPrograms';

const TamilNaduMap = ({ highlightedDistricts = [], onDistrictClick, showTooltip = true, showDetailedPopup = false }) => {
    const [hoveredDistrict, setHoveredDistrict] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [tooltipAlignment, setTooltipAlignment] = useState({ horizontal: 'center', vertical: 'top' });

    // District coordinates precisely matched to Tamil Nadu map reference
    // Using left (x) and top (y) percentages based on actual geographic positions
    // Verified against detailed Tamil Nadu district map with labeled locations
    const districts = [
        // NORTHEAST - Chennai and surrounding (Top-Right coastal area)
        { id: 'chennai', name: 'Chennai', x: 82, y: 13, width: 4, height: 4 },
        { id: 'tiruvallur', name: 'Tiruvallur', x: 78, y: 10, width: 4, height: 4 },
        { id: 'kanchipuram', name: 'Kanchipuram', x: 76, y: 16, width: 4, height: 4 },
        { id: 'chengalpattu', name: 'Chengalpattu', x: 78, y: 20, width: 4, height: 4 },
        
        // NORTH INTERIOR - Vellore region
        { id: 'ranipet', name: 'Ranipet', x: 67, y: 15, width: 4, height: 4 },
        { id: 'vellore', name: 'Vellore', x: 57, y: 15, width: 4, height: 4 },
        { id: 'tirupattur', name: 'Tirupattur', x: 51, y: 19, width: 4, height: 4 },
        
        // NORTHWEST - Top-left region
        { id: 'krishnagiri', name: 'Krishnagiri', x: 38, y: 18, width: 4, height: 4 },
        { id: 'dharmapuri', name: 'Dharmapuri', x: 40, y: 24, width: 4, height: 4 },
        
        // EAST CENTRAL - Interior to coast 
        { id: 'tiruvannamalai', name: 'Tiruvannamalai', x: 58, y: 22, width: 4, height: 4 },
        { id: 'kallakurichi', name: 'Kallakurichi', x: 60, y: 30, width: 4, height: 4 },
        { id: 'villupuram', name: 'Villupuram', x: 68, y: 25, width: 4, height: 4 },
        { id: 'cuddalore', name: 'Cuddalore', x: 70, y: 34, width: 4, height: 4 },
        
        // CENTRAL - Salem region
        { id: 'salem', name: 'Salem', x: 40, y: 31, width: 4, height: 4 },
        { id: 'namakkal', name: 'Namakkal', x: 40, y: 37, width: 4, height: 4 },
        { id: 'erode', name: 'Erode', x: 28, y: 34, width: 4, height: 4 },
        
        // WEST - Coimbatore region (Left side)
        { id: 'nilgiris', name: 'Nilgiris', x: 13, y: 34, width: 4, height: 4 },
        { id: 'coimbatore', name: 'Coimbatore', x: 18, y: 40, width: 4, height: 4 },
        { id: 'tiruppur', name: 'Tiruppur', x: 26, y: 42, width: 4, height: 4 },
        
        // CENTRAL EAST - Trichy region
        { id: 'karur', name: 'Karur', x: 40, y: 42, width: 4, height: 4 },
        { id: 'perambalur', name: 'Perambalur', x: 56, y: 37, width: 4, height: 4 },
        { id: 'ariyalur', name: 'Ariyalur', x: 62, y: 39, width: 4, height: 4 },
        { id: 'tiruchirappalli', name: 'Tiruchirappalli', x: 50, y: 40, width: 4, height: 4 },
        
        // EAST COAST - Southern coastal districts
        { id: 'mayiladuthurai', name: 'Mayiladuthurai', x: 73, y: 39, width: 4, height: 4 },
        { id: 'nagapattinam', name: 'Nagapattinam', x: 74, y: 47, width: 4, height: 4 },
        { id: 'thanjavur', name: 'Thanjavur', x: 61, y: 44, width: 4, height: 4 },
        { id: 'tiruvarur', name: 'Tiruvarur', x: 68, y: 45, width: 4, height: 4 },
        { id: 'pudukkottai', name: 'Pudukkottai', x: 56, y: 48, width: 4, height: 4 },
        
        // SOUTH CENTRAL - Madurai region
        { id: 'dindigul', name: 'Dindigul', x: 35, y: 48, width: 4, height: 4 },
        { id: 'theni', name: 'Theni', x: 28, y: 55, width: 4, height: 4 },
        { id: 'madurai', name: 'Madurai', x: 38, y: 55, width: 4, height: 4 },
        { id: 'sivaganga', name: 'Sivaganga', x: 50, y: 55, width: 4, height: 4 },
        { id: 'virudhunagar', name: 'Virudhunagar', x: 36, y: 62, width: 4, height: 4 },
        { id: 'ramanathapuram', name: 'Ramanathapuram', x: 52, y: 63, width: 4, height: 4 },
        
        // DEEP SOUTH - Bottom districts
        { id: 'thoothukudi', name: 'Thoothukudi', x: 38, y: 68, width: 4, height: 4 },
        { id: 'tirunelveli', name: 'Tirunelveli', x: 31, y: 74, width: 4, height: 4 },
        { id: 'tenkasi', name: 'Tenkasi', x: 27, y: 67, width: 4, height: 4 },
        { id: 'kanyakumari', name: 'Kanyakumari', x: 25, y: 77, width: 4, height: 4 }
    ];

    const handleMouseMove = (e, district) => {
        if (showTooltip || showDetailedPopup) {
            const container = e.currentTarget.parentElement;
            const rect = container.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            
            // Determine tooltip alignment based on position
            // For detailed popups, we need more space
            const popupWidth = showDetailedPopup ? 350 : 150;
            const popupHeight = showDetailedPopup ? 250 : 50;
            const margin = 20;
            
            // Determine horizontal alignment
            let horizontal = 'center';
            if (mouseX < popupWidth / 2 + margin) {
                horizontal = 'left';
            } else if (mouseX > rect.width - popupWidth / 2 - margin) {
                horizontal = 'right';
            }
            
            // Determine vertical alignment
            let vertical = 'top';
            if (mouseY < popupHeight + margin) {
                vertical = 'bottom';
            }
            
            setTooltipPosition({
                x: mouseX,
                y: mouseY
            });
            setTooltipAlignment({ horizontal, vertical });
            setHoveredDistrict(district);
        }
    };

    const handleMouseLeave = () => {
        setHoveredDistrict(null);
    };

    const handleClick = (district) => {
        // Only trigger click if onDistrictClick is provided and detailed popup is not enabled
        if (onDistrictClick && !showDetailedPopup) {
            onDistrictClick(district);
        }
    };

    const isHighlighted = (districtId) => {
        return highlightedDistricts.includes(districtId);
    };

    return (
        <div className="tamilnadu-map-container">
            <div className="map-legend">
                <div className="legend-item">
                    <div className="legend-color" style={{ background: 'rgba(95, 168, 211, 0.6)' }}></div>
                    <span>All Districts</span>
                </div>
                {highlightedDistricts.length > 0 && (
                    <div className="legend-item">
                        <div className="legend-color highlighted-color" style={{ background: 'rgba(255, 82, 82, 0.7)' }}></div>
                        <span>Active Programs</span>
                    </div>
                )}
                <div className="legend-note">
                    <i className="fas fa-info-circle"></i>
                    <span>{showDetailedPopup ? 'Hover over districts to see program details' : 'Hover over districts to see names • Click to view details'}</span>
                </div>
            </div>

            <div className="map-wrapper">
                <img 
                    src="/images/tamilnadumap3d.jfif"
                    alt="Tamil Nadu Map"
                    className="map-image"
                />
                
                {/* Clickable district overlays */}
                {districts.map((district) => (
                    <div
                        key={district.id}
                        className={`district-overlay ${isHighlighted(district.id) ? 'highlighted' : ''}`}
                        style={{
                            left: `${district.x}%`,
                            top: `${district.y}%`,
                            width: `${district.width}%`,
                            height: `${district.height}%`
                        }}
                        onMouseMove={(e) => handleMouseMove(e, district)}
                        onMouseLeave={handleMouseLeave}
                        onClick={() => handleClick(district)}
                        title={district.name}
                    >
                        {isHighlighted(district.id) && (
                            <div className="district-marker">
                                <i className="fas fa-map-marker-alt"></i>
                            </div>
                        )}
                    </div>
                ))}

                {/* Tooltip or Detailed Popup */}
                {hoveredDistrict && (
                    <div
                        className={`district-tooltip ${showDetailedPopup ? 'detailed-popup' : ''} 
                            align-${tooltipAlignment.horizontal} align-${tooltipAlignment.vertical}`}
                        style={{
                            left: `${tooltipPosition.x}px`,
                            top: `${tooltipPosition.y}px`
                        }}
                    >
                        {showDetailedPopup && districtPrograms[hoveredDistrict.id] ? (
                            <div className="popup-content">
                                <h3 className="popup-title">
                                    {hoveredDistrict.name}
                                    <span className="popup-title-tamil">{districtPrograms[hoveredDistrict.id].tamilName}</span>
                                </h3>
                                
                                {/* Education Programs */}
                                {districtPrograms[hoveredDistrict.id].programs.education.length > 0 && (
                                    <div className="popup-section">
                                        <h4><i className="fas fa-graduation-cap"></i> Education</h4>
                                        <ul>
                                            {districtPrograms[hoveredDistrict.id].programs.education.map((program, idx) => (
                                                <li key={idx}>{program}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                {/* Food Programs */}
                                {districtPrograms[hoveredDistrict.id].programs.food.length > 0 && (
                                    <div className="popup-section">
                                        <h4><i className="fas fa-bowl-rice"></i> Food</h4>
                                        <ul>
                                            {districtPrograms[hoveredDistrict.id].programs.food.map((program, idx) => (
                                                <li key={idx}>{program}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                {/* Technology Programs */}
                                {districtPrograms[hoveredDistrict.id].programs.technology.length > 0 && (
                                    <div className="popup-section">
                                        <h4><i className="fas fa-computer"></i> Technology</h4>
                                        <ul>
                                            {districtPrograms[hoveredDistrict.id].programs.technology.map((program, idx) => (
                                                <li key={idx}>{program}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                {/* No programs message */}
                                {districtPrograms[hoveredDistrict.id].programs.education.length === 0 &&
                                 districtPrograms[hoveredDistrict.id].programs.food.length === 0 &&
                                 districtPrograms[hoveredDistrict.id].programs.technology.length === 0 && (
                                    <p className="no-programs">No active programs in this district yet</p>
                                )}
                            </div>
                        ) : (
                            // Simple tooltip with just district name
                            hoveredDistrict.name
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TamilNaduMap;
